// Utility functions for seasonal date management

/**
 * Determines which season a given date falls into based on property's seasonal configuration
 * @param {Date|string} date - The date to check
 * @param {Object} seasonalDates - The seasonal dates configuration from property.price.seasonalDates
 * @returns {string|null} - 'lowSeason', 'midSeason', 'highSeason', or null if no match
 */
export const getSeasonForDate = (date, seasonalDates) => {
    if (!date || !seasonalDates) return null;

    const checkDate = new Date(date);
    const checkMonth = checkDate.getMonth() + 1; // getMonth() returns 0-11, we need 1-12
    const checkDay = checkDate.getDate();

    const seasons = ['highSeason', 'midSeason', 'lowSeason']; // Check high season first (priority)

    for (const season of seasons) {
        if (seasonalDates[season] && Array.isArray(seasonalDates[season])) {
            for (const period of seasonalDates[season]) {
                if (period.startMonth && period.startDay && period.endMonth && period.endDay) {
                    const startMonth = parseInt(period.startMonth);
                    const startDay = parseInt(period.startDay);
                    const endMonth = parseInt(period.endMonth);
                    const endDay = parseInt(period.endDay);

                    // Handle same year periods
                    if (startMonth <= endMonth) {
                        if (checkMonth > startMonth && checkMonth < endMonth) {
                            return season;
                        }
                        if (checkMonth === startMonth && checkDay >= startDay) {
                            if (checkMonth < endMonth || (checkMonth === endMonth && checkDay <= endDay)) {
                                return season;
                            }
                        }
                        if (checkMonth === endMonth && checkDay <= endDay && checkMonth > startMonth) {
                            return season;
                        }
                        if (checkMonth === startMonth && checkMonth === endMonth && checkDay >= startDay && checkDay <= endDay) {
                            return season;
                        }
                    } else {
                        // Handle year-crossing periods (e.g., Dec 15 - Feb 28)
                        if (checkMonth > startMonth || checkMonth < endMonth) {
                            return season;
                        }
                        if (checkMonth === startMonth && checkDay >= startDay) {
                            return season;
                        }
                        if (checkMonth === endMonth && checkDay <= endDay) {
                            return season;
                        }
                    }
                }
            }
        }
    }

    return null; // No season found, use default pricing
};

/**
 * Gets the appropriate nightly price for a given date based on seasonal configuration
 * @param {Date|string} date - The date to get pricing for
 * @param {Object} property - The property object with price and seasonal data
 * @returns {number|null} - The nightly price or null if no price available
 */
export const getNightlyPriceForDate = (date, property) => {
    if (!property || !property.price) return null;

    const season = getSeasonForDate(date, property.price.seasonalDates);

    switch (season) {
        case 'highSeason':
            return property.price.nightlyHigh;
        case 'midSeason':
            return property.price.nightlyMid;
        case 'lowSeason':
            return property.price.nightlyLow;
        default:
            return property.price.nightlyMid; // Default to mid-season if no season matches
    }
};

/**
 * Gets all date ranges for a specific season
 * @param {Object} seasonalDates - The seasonal dates configuration
 * @param {string} season - 'lowSeason', 'midSeason', or 'highSeason'
 * @returns {Array} - Array of date ranges for the specified season
 */
export const getSeasonDateRanges = (seasonalDates, season) => {
    if (!seasonalDates || !seasonalDates[season]) return [];

    return seasonalDates[season].filter(period => period.startMonth && period.startDay && period.endMonth && period.endDay);
};

/**
 * Checks if two date ranges overlap
 * @param {Object} range1 - First date range with startMonth, startDay, endMonth, endDay
 * @param {Object} range2 - Second date range with startMonth, startDay, endMonth, endDay
 * @returns {boolean} - True if ranges overlap
 */
export const doDateRangesOverlap = (range1, range2) => {
    // Convert month/day to comparable numbers (assuming same year for comparison)
    const start1 = parseInt(range1.startMonth) * 100 + parseInt(range1.startDay);
    const end1 = parseInt(range1.endMonth) * 100 + parseInt(range1.endDay);
    const start2 = parseInt(range2.startMonth) * 100 + parseInt(range2.startDay);
    const end2 = parseInt(range2.endMonth) * 100 + parseInt(range2.endDay);

    // Handle simple case where neither range crosses year boundary
    if (start1 <= end1 && start2 <= end2) {
        return start1 <= end2 && end1 >= start2;
    }

    // Handle year-crossing ranges (more complex logic needed)
    // For now, we'll use a simplified approach
    return start1 <= end2 || end1 >= start2 || start2 <= end1 || end2 >= start1;
};

/**
 * Validates seasonal dates configuration for conflicts
 * @param {Object} seasonalDates - The seasonal dates configuration to validate
 * @returns {Array} - Array of validation errors (empty if no errors)
 */
export const validateSeasonalDates = (seasonalDates) => {
    const errors = [];
    const allRanges = [];

    // Collect all date ranges with their season info
    Object.keys(seasonalDates).forEach(season => {
        if (seasonalDates[season] && Array.isArray(seasonalDates[season])) {
            seasonalDates[season].forEach((period, index) => {
                if (period.startDate && period.endDate) {
                    const startDate = new Date(period.startDate);
                    const endDate = new Date(period.endDate);

                    // Check if start date is before end date
                    if (startDate >= endDate) {
                        errors.push(`${season} période ${index + 1}: La date de début doit être antérieure à la date de fin`);
                    }

                    allRanges.push({
                        season,
                        index,
                        startDate,
                        endDate,
                        period
                    });
                }
            });
        }
    });

    // Check for overlapping ranges
    for (let i = 0; i < allRanges.length; i++) {
        for (let j = i + 1; j < allRanges.length; j++) {
            if (doDateRangesOverlap(allRanges[i].period, allRanges[j].period)) {
                errors.push(
                    `Conflit de dates: ${allRanges[i].season} période ${allRanges[i].index + 1} ` +
                    `chevauche avec ${allRanges[j].season} période ${allRanges[j].index + 1}`
                );
            }
        }
    }

    return errors;
}; 